import React, { useState, useRef, useEffect } from "react";
import {
  Box, Typography, Button, Paper, Grid, FormControl, InputLabel,
  Select, MenuItem, TextField, CircularProgress, Alert, ButtonBase
} from "@mui/material";
import api from "../../api";

const sanctionLabels = {
  0: "",
  1: "AVERTISSEMENT",
  2: "MISE EN GARDE",
  3: "DISQUALIFICATION"
};

export default function BoxedeuxBoxeurs({ onFinish, onBack, classesList = [], getElevesFromClasse, initialClasseId, isStudentSession = false }) {
  const [mode, setMode] = useState("settings");
  const [nbReprises, setNbReprises] = useState(1);
  const [durationMin, setDurationMin] = useState(1);
  const [durationSec, setDurationSec] = useState(0);
  const [type, setType] = useState("poings");
  const [valeurPoing, setValeurPoing] = useState(1);
  const [valeurPied, setValeurPied] = useState(2);

  const [availableClasses, setAvailableClasses] = useState([]);
  const [loadingClasses, setLoadingClasses] = useState(false);
  const [classesError, setClassesError] = useState("");
  const [classeId, setClasseId] = useState(initialClasseId || "");

  const [eleves, setEleves] = useState([]);
  const [loadingEleves, setLoadingEleves] = useState(false);
  const [elevesFetchError, setElevesFetchError] = useState("");

  const [eleveRed, setEleveRed] = useState("");
  const [eleveBlue, setEleveBlue] = useState("");
  const [arbitreId, setArbitreId] = useState("");

  const [nomRed, setNomRed] = useState("");
  const [nomBlue, setNomBlue] = useState("");

  const [settingsError, setSettingsError] = useState("");

  const [currentRound, setCurrentRound] = useState(1);
  const [time, setTime] = useState(0);
  const [running, setRunning] = useState(false);
  const [rounds, setRounds] = useState([]);
  const timerRef = useRef();
  const audioRef = useRef();

  function normalizeClasses(raw = []) {
    return raw.map(c => {
      const id = String(c._id ?? c.id ?? c.classeId ?? c.uuid ?? "");
      const nom = c.nom ?? c.name ?? c.label ?? c.title ?? `Classe ${id}`;
      return { id, nom, raw: c };
    }).filter(x => x.id);
  }

  function normalizeEleves(raw = []) {
    return raw.map(e => {
      const id = String(e._id ?? e.id ?? e.id_eleve ?? e.uuid ?? "");
      const nom = `${e.prenom ? e.prenom + " " : ""}${e.nom ?? e.name ?? e.fullname ?? id}`;
      return { id, nom, raw: e };
    }).filter(x => x.id);
  }

  async function loadClasses(force = false) {
    if (!force && Array.isArray(classesList) && classesList.length > 0) {
      setAvailableClasses(normalizeClasses(classesList));
      if (initialClasseId) {
        setClasseId(initialClasseId);
      }
      return;
    }
    setLoadingClasses(true);
    setClassesError("");
    try {
      const res = await api.get("/classes");
      const raw = Array.isArray(res.data) ? res.data : [];
      setAvailableClasses(normalizeClasses(raw));
    } catch (err) {
      console.error("BoxedeuxBoxeurs: GET /classes failed", err);
      setClassesError(err?.response?.data?.error || err?.message || "Impossible de charger les classes.");
      setAvailableClasses([]);
    } finally {
      setLoadingClasses(false);
    }
  }

  useEffect(() => { loadClasses(false); }, []); // eslint-disable-line

  async function tryFetchEleves(id) {
    const attempts = [
      { url: `/jugeboxe/eleves?classe=${encodeURIComponent(id)}`, hint: "jugeboxe/eleves?classe=" },
      { url: `/classes/${encodeURIComponent(id)}/eleves`, hint: "classes/:id/eleves" },
      { url: `/classes/${encodeURIComponent(id)}/students`, hint: "classes/:id/students" },
      { url: `/eleves?classe=${encodeURIComponent(id)}`, hint: "eleves?classe=" },
      { url: `/eleves?classeId=${encodeURIComponent(id)}`, hint: "eleves?classeId=" },
      { url: `/students?classe=${encodeURIComponent(id)}`, hint: "students?classe=" },
      { url: `/students?classId=${encodeURIComponent(id)}`, hint: "students?classId=" },
    ];

    for (const a of attempts) {
      try {
        const res = await api.get(a.url);
        if (Array.isArray(res.data)) return { list: res.data, used: a.hint, url: a.url };
        if (res.data && Array.isArray(res.data.items)) return { list: res.data.items, used: a.hint, url: a.url };
        if (res.data && Array.isArray(res.data.data)) return { list: res.data.data, used: a.hint, url: a.url };
        if (res.data && typeof res.data === "object") {
          const arrProp = Object.keys(res.data).find(k => Array.isArray(res.data[k]));
          if (arrProp) return { list: res.data[arrProp], used: a.hint, url: a.url };
        }
        return { list: [], used: a.hint, url: a.url };
      } catch (err) {
        console.warn(`BoxedeuxBoxeurs: attempt ${a.hint} failed`, err?.response?.status, err?.message);
      }
    }
    throw new Error("Aucun endpoint valide trouvé pour récupérer les élèves (voir console pour tentatives).");
  }

  async function fetchElevesForClasse(id) {
    setElevesFetchError("");
    if (!id) {
      setEleves([]);
      setEleveRed("");
      setEleveBlue("");
      setArbitreId("");
      return;
    }
    setLoadingEleves(true);
    try {
      let list = [];
      if (getElevesFromClasse && typeof getElevesFromClasse === "function") {
        list = await getElevesFromClasse(id) || [];
      } else {
        const resp = await tryFetchEleves(id);
        list = resp.list ?? [];
      }

      if (!Array.isArray(list)) {
        setEleves([]);
        setElevesFetchError("Format inattendu reçu pour la liste d'élèves (voir console).");
        return;
      }

      const normalized = normalizeEleves(list);
      setEleves(normalized);
      setElevesFetchError("");
      if (normalized.length > 0) {
        setEleveRed(prev => prev || normalized[0].id);
        setEleveBlue(prev => prev || (normalized.length > 1 ? normalized[1].id : normalized[0].id));
      } else {
        setEleveRed("");
        setEleveBlue("");
      }
    } catch (err) {
      console.error("BoxedeuxBoxeurs: failed loading eleves", err);
      setEleves([]);
      setEleveRed("");
      setEleveBlue("");
      setElevesFetchError(err?.message || "Impossible de charger les élèves.");
    } finally {
      setLoadingEleves(false);
    }
  }

  useEffect(() => {
    if (classeId) fetchElevesForClasse(classeId);
    else {
      setEleves([]);
      setEleveRed("");
      setEleveBlue("");
      setArbitreId("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [classeId]);

  const initialTimeSec = () => (Number(durationMin || 0) * 60) + Number(durationSec || 0);

  function createEmptyRounds(count) {
    const c = Number(count) || 1;
    return Array(c).fill().map(() => ({
      scoreRed: { poings: 0, pieds: 0 },
      scoreBlue: { poings: 0, pieds: 0 },
      sanctionsRed: 0,
      sanctionsBlue: 0,
      endedBySanction: false
    }));
  }

  function startMatch() {
    setSettingsError("");
    if (isStudentSession && !classeId) {
      setSettingsError("La classe liée au QR élève n'a pas été détectée.");
      return;
    }
    if (classeId) {
      if (!eleveBlue || !eleveRed) { setSettingsError("Sélectionnez deux boxeurs."); return; }
      if (eleveBlue === eleveRed) { setSettingsError("Le boxeur bleu et le boxeur rouge doivent être différents."); return; }
      if (arbitreId && (arbitreId === eleveBlue || arbitreId === eleveRed)) { setSettingsError("L'arbitre ne peut pas être l'un des boxeurs."); return; }
    } else {
      if (!nomBlue || !nomRed) { setSettingsError("Renseignez les noms des boxeurs en mode manuel."); return; }
    }
    const r = createEmptyRounds(nbReprises);
    setRounds(r);
    setCurrentRound(1);
    setTime(initialTimeSec());
    setRunning(false);
    setMode("match");
  }

  function computeWinnerFromRounds(rs) {
    let blueRounds = 0;
    let redRounds = 0;
    for (const round of rs) {
      if (round.sanctionsRed === 3 && round.sanctionsBlue !== 3) { blueRounds++; continue; }
      if (round.sanctionsBlue === 3 && round.sanctionsRed !== 3) { redRounds++; continue; }
      if (round.sanctionsRed === 3 && round.sanctionsBlue === 3) { continue; }
      const totalRed = (round.scoreRed.poings || 0) + (round.scoreRed.pieds || 0);
      const totalBlue = (round.scoreBlue.poings || 0) + (round.scoreBlue.pieds || 0);
      if (totalBlue > totalRed) blueRounds++;
      else if (totalRed > totalBlue) redRounds++;
    }
    if (blueRounds > redRounds) return "Bleu";
    if (redRounds > blueRounds) return "Rouge";
    return "Égalité";
  }

  function finishMatch() {
    const sanitizeParticipantName = (value) => {
      if (!value || value === "—") return null;
      const text = String(value).trim();
      if (!text || /^[a-f0-9]{24}$/i.test(text)) return null;
      return text;
    };

    const winnerLabel = computeWinnerFromRounds(rounds);
    let winnerName = "—";
    if (winnerLabel === "Bleu") {
      winnerName = classeId ? (eleves.find(e => e.id === eleveBlue)?.nom || eleveBlue) : (nomBlue || "Bleu");
    } else if (winnerLabel === "Rouge") {
      winnerName = classeId ? (eleves.find(e => e.id === eleveRed)?.nom || eleveRed) : (nomRed || "Rouge");
    } else if (winnerLabel === "Égalité") {
      winnerName = "Égalité";
    }
    const participants = {
      blueId: classeId ? (eleveBlue || null) : null,
      redId: classeId ? (eleveRed || null) : null,
      arbitreId: classeId ? (arbitreId || null) : null,
      nomBlue: sanitizeParticipantName(displayBlue()),
      nomRed: sanitizeParticipantName(displayRed()),
      nomArbitre: sanitizeParticipantName(displayArbitre()),
    };
    const params = { nbReprises, durationMin, durationSec, type, valeurPoing, valeurPied, classeId: classeId || null, activity: "Boxe Française", nbBoxeurs: 2 };
    const result = { params, rounds, participants, winner: winnerLabel, winnerName, createdAt: new Date().toISOString() };
    if (typeof onFinish === "function") onFinish(result);
    setMode("settings");
  }

  useEffect(() => {
    if (mode !== "match") return;
    if (running && time > 0) { timerRef.current = setInterval(() => setTime(t => t - 1), 1000); }
    if ((!running || time <= 0) && timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
    if (time === 0 && running) {
      setRunning(false);
      if (audioRef.current) audioRef.current.play().catch(() => {});
      setTimeout(() => {
        if (currentRound < rounds.length) {
          setCurrentRound(c => c + 1);
          setTime(initialTimeSec());
          setRunning(false);
        } else {
          finishMatch();
        }
      }, 800);
    }
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [running, time, mode, currentRound, rounds]);

  function handleScore(color, typeName, value = 1) {
    const idx = currentRound - 1;
    setRounds(rs => rs.map((r, i) => {
      if (i !== idx) return r;
      const key = color === "red" ? "scoreRed" : "scoreBlue";
      const newScore = { ...(r[key] || {}) };
      newScore[typeName] = Math.max(0, (newScore[typeName] || 0) + value);
      return { ...r, [key]: newScore };
    }));
  }

  function touchHandlers(action) {
    return {
      onClick: () => {
        action();
      }
    };
  }

  function handleSanction(color, delta) {
    const idx = currentRound - 1;
    setRounds(prev => prev.map((r, i) => {
      if (i !== idx) return r;
      let sr = r.sanctionsRed || 0;
      let sb = r.sanctionsBlue || 0;
      if (color === "red") sr = Math.max(0, Math.min(3, sr + delta));
      else sb = Math.max(0, Math.min(3, sb + delta));
      const newR = { ...r, sanctionsRed: sr, sanctionsBlue: sb };
      if (sr === 3 || sb === 3) {
        newR.endedBySanction = true;
        setTimeout(() => { finishMatch(); }, 600);
      }
      return newR;
    }));
    setRunning(false);
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  }

  function resetCurrentRound() {
    const idx = currentRound - 1;
    setRounds(rs => rs.map((r, i) => i === idx ? {
      scoreRed: { poings: 0, pieds: 0 },
      scoreBlue: { poings: 0, pieds: 0 },
      sanctionsRed: 0,
      sanctionsBlue: 0,
      endedBySanction: false
    } : r));
    setTime(initialTimeSec());
    setRunning(false);
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  }

  const displayBlue = () => (classeId ? (eleves.find(e => e.id === eleveBlue)?.nom || eleveBlue) : (nomBlue || "Bleu"));
  const displayRed = () => (classeId ? (eleves.find(e => e.id === eleveRed)?.nom || eleveRed) : (nomRed || "Rouge"));
  const displayArbitre = () => (classeId ? (eleves.find(e => e.id === arbitreId)?.nom || arbitreId) : "—");

  function sanctionColorClass(count) {
    if (count >= 2) return "error";
    if (count === 1) return "warning";
    return "success";
  }

  const isThreeColumns = type === "piedspoings";
  const actionColumns = isThreeColumns ? "repeat(3, minmax(0, 1fr))" : "repeat(2, minmax(0, 1fr))";
  const primaryButtonHeight = isThreeColumns ? { xs: 150, sm: 168 } : { xs: 170, sm: 186 };
  const primaryIconSize = isThreeColumns ? { xs: 42, sm: 48 } : { xs: 54, sm: 60 };
  const primaryLabelSize = isThreeColumns ? { xs: 14, sm: 16 } : { xs: 16, sm: 18 };
  const primaryScoreSize = isThreeColumns ? { xs: 26, sm: 30 } : { xs: 32, sm: 36 };
  const primaryDeltaSize = isThreeColumns ? { xs: 12, sm: 13 } : { xs: 13, sm: 15 };
  const penaltyTopSize = isThreeColumns ? { xs: 28, sm: 32 } : { xs: 34, sm: 38 };
  const secondaryButtonHeight = isThreeColumns ? 36 : 40;
  const fighterTitleMinHeight = { xs: 56, sm: 64 };
  const fighterPanelPadding = { xs: 1.25, sm: 1.75 };
  const primaryGridSx = {
    display: "grid",
    gridTemplateColumns: actionColumns,
    gap: 1,
    width: "100%",
    alignItems: "stretch",
    gridAutoRows: "1fr",
  };
  const secondaryGridSx = {
    display: "grid",
    gridTemplateColumns: actionColumns,
    gap: 1.2,
    width: "100%",
    mt: 0.9,
    gridAutoRows: "1fr",
  };
  const actionCellSx = {
    minWidth: 0,
    width: "100%",
    display: "flex",
  };
  const secondaryButtonSx = {
    minHeight: secondaryButtonHeight,
    fontSize: isThreeColumns ? 12 : 14,
    fontWeight: 700,
    borderRadius: 2.5,
  };

  function renderScoreButton({ color, typeName, label, imageSrc, imageAlt, value, score, bgColor, borderColor, textColor }) {
    return (
      <ButtonBase
        {...touchHandlers(() => handleScore(color, typeName, value))}
        aria-label={`Ajouter ${label.toLowerCase()} ${color === "red" ? "rouge" : "bleu"}`}
        sx={{
          width: "100%",
          minHeight: primaryButtonHeight,
          height: "100%",
          borderRadius: 3,
          bgcolor: bgColor,
          border: `2px solid ${borderColor}`,
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          gap: isThreeColumns ? 0.45 : 0.6,
          py: isThreeColumns ? 1 : 1.2,
          px: isThreeColumns ? 0.5 : 0.75,
          touchAction: "manipulation",
          WebkitTapHighlightColor: "transparent",
        }}
      >
        <Box
          component="img"
          src={imageSrc}
          alt={imageAlt}
          sx={{
            width: primaryIconSize,
            height: primaryIconSize,
            objectFit: "contain"
          }}
        />
        <Typography sx={{ fontSize: primaryLabelSize, fontWeight: 900, lineHeight: 1.1 }}>{label}</Typography>
        <Typography sx={{ fontSize: primaryScoreSize, fontWeight: 900, lineHeight: 1 }}>{score}</Typography>
        <Typography sx={{ fontSize: primaryDeltaSize, color: textColor, lineHeight: 1.1 }}>+{value}</Typography>
      </ButtonBase>
    );
  }

  function renderPenaltyButton({ color, count }) {
    const palette = sanctionColorClass(count) === "error"
      ? { bgColor: "#c62828", borderColor: "#a91f1f" }
      : sanctionColorClass(count) === "warning"
        ? { bgColor: "#ed6c02", borderColor: "#d05f02" }
        : { bgColor: "#2e7d32", borderColor: "#256728" };

    return (
      <ButtonBase
        {...touchHandlers(() => handleSanction(color, 1))}
        aria-label={`Ajouter pénalité ${color === "red" ? "rouge" : "bleu"}`}
        sx={{
          width: "100%",
          minHeight: primaryButtonHeight,
          height: "100%",
          fontSize: primaryLabelSize,
          fontWeight: 900,
          borderRadius: 3,
          lineHeight: 1.15,
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          gap: isThreeColumns ? 0.45 : 0.6,
          color: "#fff",
          bgcolor: palette.bgColor,
          border: `2px solid ${palette.borderColor}`,
          touchAction: "manipulation",
          px: isThreeColumns ? 0.5 : 0.75,
          py: isThreeColumns ? 1 : 1.2,
          textAlign: "center",
          WebkitTapHighlightColor: "transparent",
        }}
      >
        <Box component="span" sx={{ fontSize: penaltyTopSize, lineHeight: 1 }}>+1</Box>
        <Box component="span">Pénalité</Box>
        <Box component="span" sx={{ fontSize: primaryScoreSize, lineHeight: 1 }}>{count}</Box>
        <Box component="span" sx={{ fontSize: primaryDeltaSize }}>{sanctionLabels[count] || "AUCUNE"}</Box>
      </ButtonBase>
    );
  }

  if (mode === "settings") {
    return (
      <Box sx={{ minHeight: "100vh", bgcolor: "#fff", display: "flex", justifyContent: "center", alignItems: "center" }}>
        <Paper sx={{ p: 3, minWidth: 320, width: "95%", maxWidth: 900 }}>
          <Typography variant="h4" align="center" sx={{ mb: 2 }}>Paramètres du match 2 boxeurs</Typography>

          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle1">Nombre de reprises</Typography>
            <TextField type="number" value={nbReprises} onChange={e => setNbReprises(Math.max(1, Number(e.target.value)))} inputProps={{ min: 1, style: { width: 64, textAlign: "center" } }} sx={{ bgcolor: "#fff", borderRadius: 1, mt: 1 }} />
          </Box>

          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle1">Durée d'une reprise</Typography>
            <Grid container spacing={1} alignItems="center">
              <Grid item>
                <TextField type="number" value={durationMin} onChange={e => setDurationMin(Math.max(0, Number(e.target.value)))} inputProps={{ min: 0, style: { width: 60, textAlign: "center" } }} />
              </Grid>
              <Grid item><Typography>minutes</Typography></Grid>
              <Grid item>
                <TextField type="number" value={durationSec} onChange={e => setDurationSec(Math.max(0, Math.min(59, Number(e.target.value))))} inputProps={{ min: 0, max: 59, style: { width: 60, textAlign: "center" } }} />
              </Grid>
              <Grid item><Typography>secondes</Typography></Grid>
            </Grid>
          </Box>

          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle1">Type de match</Typography>
            <FormControl fullWidth sx={{ mt: 1 }}>
              <Select value={type} onChange={e => setType(e.target.value)} size="small">
                <MenuItem value="poings">Poings</MenuItem>
                <MenuItem value="piedspoings">Pieds/Poings</MenuItem>
              </Select>
            </FormControl>
          </Box>

          {type === "piedspoings" && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle1">Valeur des coups</Typography>
              <Grid container spacing={2} alignItems="center">
                <Grid item><TextField label="Poing" type="number" value={valeurPoing} onChange={e => setValeurPoing(Math.max(0, Number(e.target.value)))} size="small" /></Grid>
                <Grid item><TextField label="Pied" type="number" value={valeurPied} onChange={e => setValeurPied(Math.max(0, Number(e.target.value)))} size="small" /></Grid>
              </Grid>
            </Box>
          )}

          <Box sx={{ mt: 1, mb: 2 }}>
            {!isStudentSession && <Typography variant="subtitle1">Sélection de la classe (optionnel)</Typography>}

            {loadingClasses ? (
              <Box sx={{ display: "flex", alignItems: "center", gap: 2, my: 1 }}>
                <CircularProgress size={20} /><Typography>Chargement des classes...</Typography>
              </Box>
            ) : classesError ? (
              <Box sx={{ mb: 2 }}><Alert severity="error">{classesError}</Alert><Button onClick={() => loadClasses(true)} variant="outlined" size="small">Réessayer</Button></Box>
            ) : (
              <>
                {isStudentSession ? null : (
                  <FormControl fullWidth sx={{ mb: 2 }}>
                    <InputLabel>Classe</InputLabel>
                    <Select value={classeId} label="Classe" onChange={e => setClasseId(e.target.value)} size="small">
                      <MenuItem value="">Aucune</MenuItem>
                      {availableClasses.map(c => <MenuItem key={c.id} value={c.id}>{c.nom}</MenuItem>)}
                    </Select>
                  </FormControl>
                )}

                {classeId ? (
                  <>
                    {loadingEleves ? <Typography>Chargement des élèves...</Typography> :
                      elevesFetchError ? <Alert severity="error">{elevesFetchError}</Alert> : (
                        <Grid container spacing={2} sx={{ mb: 1 }}>
                          <Grid item xs={12} md={4}>
                            <FormControl fullWidth>
                              <InputLabel>Élève Bleu</InputLabel>
                              <Select value={eleveBlue} label="Élève Bleu" onChange={e => setEleveBlue(e.target.value)} size="small">
                                <MenuItem value="">Aucun</MenuItem>
                                {eleves.map(el => <MenuItem key={el.id} value={el.id}>{el.nom}</MenuItem>)}
                              </Select>
                            </FormControl>
                          </Grid>
                          <Grid item xs={12} md={4}>
                            <FormControl fullWidth>
                              <InputLabel>Élève Rouge</InputLabel>
                              <Select value={eleveRed} label="Élève Rouge" onChange={e => setEleveRed(e.target.value)} size="small">
                                <MenuItem value="">Aucun</MenuItem>
                                {eleves.map(el => <MenuItem key={el.id} value={el.id}>{el.nom}</MenuItem>)}
                              </Select>
                            </FormControl>
                          </Grid>
                          <Grid item xs={12} md={4}>
                            <FormControl fullWidth sx={{ minWidth: 200 }}>
                              <InputLabel>Arbitre (optionnel)</InputLabel>
                              <Select value={arbitreId} label="Arbitre (optionnel)" onChange={e => setArbitreId(e.target.value)} size="small">
                                <MenuItem value="">— Aucun —</MenuItem>
                                {eleves.map(el => <MenuItem key={el.id} value={el.id}>{el.nom}</MenuItem>)}
                              </Select>
                            </FormControl>
                          </Grid>
                        </Grid>
                      )}
                  </>
                ) : !isStudentSession ? (
                  <Grid container spacing={2} sx={{ mb: 2 }}>
                    <Grid item xs={12} md={6}>
                      <TextField label="Nom Bleu" value={nomBlue} onChange={e => setNomBlue(e.target.value)} fullWidth size="small" />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField label="Nom Rouge" value={nomRed} onChange={e => setNomRed(e.target.value)} fullWidth size="small" />
                    </Grid>
                  </Grid>
                ) : null}
              </>
            )}
          </Box>

          {settingsError && <Alert severity="error" sx={{ mb: 2 }}>{settingsError}</Alert>}

          <Box sx={{ display: "flex", justifyContent: "space-between", mt: 2 }}>
            <Button variant="outlined" color="secondary" onClick={() => { if (typeof onBack === "function") onBack(); }}>Retour</Button>
            <Button variant="contained" color="primary" onClick={startMatch}>Lancer le match</Button>
          </Box>
        </Paper>
      </Box>
    );
  }

  const round = rounds[currentRound - 1] || {};

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "#f4f6fb", display: "flex", flexDirection: "column", alignItems: "center", py: 2 }}>
      <audio src="/assets/bip.mp3" ref={audioRef} preload="auto" />
      <Paper sx={{ mt: 2, p: { xs: 1.5, sm: 3 }, minWidth: 320, width: "95%", maxWidth: 900, borderRadius: 4 }}>
        <Box sx={{ textAlign: "center", mb: 2.5 }}>
          <Typography variant="h6" sx={{ fontWeight: 800 }}>
            Reprise {currentRound} / {rounds.length}
          </Typography>
          <Typography sx={{ fontSize: { xs: 42, sm: 56 }, fontWeight: 900, lineHeight: 1.1, mt: 1 }}>
            {`${Math.floor(time / 60)}:${(time % 60).toString().padStart(2, "0")}`}
          </Typography>
          <Button variant="contained" onClick={() => setRunning(r => !r)} sx={{ mt: 1.5, minWidth: 170, minHeight: 52, fontSize: 18, fontWeight: 700 }}>
            {running ? "Pause" : "Démarrer"}
          </Button>
          <Typography sx={{ mt: 1.5, fontSize: 14, color: "#555", fontWeight: 700 }}>
            Juge arbitre : {displayArbitre()}
          </Typography>
        </Box>

        <Grid container spacing={1.5}>
          <Grid item xs={12}>
            <Paper sx={{ width: "100%", bgcolor: "#fff5f5", p: fighterPanelPadding, borderRadius: 4 }}>
              <Typography sx={{ textAlign: "center", fontSize: 22, fontWeight: 900, color: "#c62828", mb: 1.5, minHeight: fighterTitleMinHeight, display: "flex", alignItems: "center", justifyContent: "center" }}>
                Rouge — {displayRed()}
              </Typography>
              <Box sx={primaryGridSx}>
                <Box sx={actionCellSx}>
                  {renderScoreButton({
                    color: "red",
                    typeName: "poings",
                    label: "Poings",
                    imageSrc: "/assets/gantrouge.png",
                    imageAlt: "Poing rouge",
                    value: valeurPoing,
                    score: round?.scoreRed?.poings ?? 0,
                    bgColor: "#ffe1e1",
                    borderColor: "#d32f2f",
                    textColor: "#8f2020",
                  })}
                </Box>

                {type === "piedspoings" && (
                  <Box sx={actionCellSx}>
                    {renderScoreButton({
                      color: "red",
                      typeName: "pieds",
                      label: "Pieds",
                      imageSrc: "/assets/whiteshoes.png",
                      imageAlt: "Pied rouge",
                      value: valeurPied,
                      score: round?.scoreRed?.pieds ?? 0,
                      bgColor: "#ffecef",
                      borderColor: "#ef5350",
                      textColor: "#b23b3a",
                    })}
                  </Box>
                )}

                <Box sx={actionCellSx}>
                  {renderPenaltyButton({ color: "red", count: round?.sanctionsRed ?? 0 })}
                </Box>
              </Box>

              <Box sx={secondaryGridSx}>
                <Box sx={actionCellSx}>
                  <Button fullWidth variant="text" color="inherit" onClick={() => handleScore("red", "poings", -valeurPoing)} sx={secondaryButtonSx}>
                    - Poing
                  </Button>
                </Box>
                {type === "piedspoings" && (
                  <Box sx={actionCellSx}>
                    <Button fullWidth variant="text" color="inherit" onClick={() => handleScore("red", "pieds", -valeurPied)} sx={secondaryButtonSx}>
                      - Pied
                    </Button>
                  </Box>
                )}
                <Box sx={actionCellSx}>
                  <Button fullWidth variant="text" color="inherit" onClick={() => handleSanction("red", -1)} sx={secondaryButtonSx}>
                    - Pénalité
                  </Button>
                </Box>
              </Box>
            </Paper>
          </Grid>

          <Grid item xs={12}>
            <Paper sx={{ width: "100%", bgcolor: "#f5faff", p: fighterPanelPadding, borderRadius: 4 }}>
              <Typography sx={{ textAlign: "center", fontSize: 22, fontWeight: 900, color: "#1565c0", mb: 1.5, minHeight: fighterTitleMinHeight, display: "flex", alignItems: "center", justifyContent: "center" }}>
                Bleu — {displayBlue()}
              </Typography>
              <Box sx={primaryGridSx}>
                <Box sx={actionCellSx}>
                  {renderScoreButton({
                    color: "blue",
                    typeName: "poings",
                    label: "Poings",
                    imageSrc: "/assets/gantbleu.png",
                    imageAlt: "Poing bleu",
                    value: valeurPoing,
                    score: round?.scoreBlue?.poings ?? 0,
                    bgColor: "#dceeff",
                    borderColor: "#1976d2",
                    textColor: "#174a7c",
                  })}
                </Box>

                {type === "piedspoings" && (
                  <Box sx={actionCellSx}>
                    {renderScoreButton({
                      color: "blue",
                      typeName: "pieds",
                      label: "Pieds",
                      imageSrc: "/assets/whiteshoes.png",
                      imageAlt: "Pied bleu",
                      value: valeurPied,
                      score: round?.scoreBlue?.pieds ?? 0,
                      bgColor: "#eef3ff",
                      borderColor: "#5a64ea",
                      textColor: "#3840b8",
                    })}
                  </Box>
                )}

                <Box sx={actionCellSx}>
                  {renderPenaltyButton({ color: "blue", count: round?.sanctionsBlue ?? 0 })}
                </Box>
              </Box>

              <Box sx={secondaryGridSx}>
                <Box sx={actionCellSx}>
                  <Button fullWidth variant="text" color="inherit" onClick={() => handleScore("blue", "poings", -valeurPoing)} sx={secondaryButtonSx}>
                    - Poing
                  </Button>
                </Box>
                {type === "piedspoings" && (
                  <Box sx={actionCellSx}>
                    <Button fullWidth variant="text" color="inherit" onClick={() => handleScore("blue", "pieds", -valeurPied)} sx={secondaryButtonSx}>
                      - Pied
                    </Button>
                  </Box>
                )}
                <Box sx={actionCellSx}>
                  <Button fullWidth variant="text" color="inherit" onClick={() => handleSanction("blue", -1)} sx={secondaryButtonSx}>
                    - Pénalité
                  </Button>
                </Box>
              </Box>
            </Paper>
          </Grid>
        </Grid>

        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.2, mt: 2.5 }}>
          <Button variant="outlined" onClick={() => { resetCurrentRound(); }} sx={{ minHeight: 48, fontWeight: 700 }}>
            Remise à zéro
          </Button>
          <Grid container spacing={1.2}>
            <Grid item xs={6}>
              <Button fullWidth disabled={currentRound === 1} onClick={() => { setCurrentRound(c => c - 1); setTime(initialTimeSec()); setRunning(false); }} sx={{ minHeight: 48 }}>
                Reprise précédente
              </Button>
            </Grid>
            <Grid item xs={6}>
              <Button fullWidth disabled={currentRound === rounds.length} onClick={() => { setCurrentRound(c => c + 1); setTime(initialTimeSec()); setRunning(false); }} sx={{ minHeight: 48 }}>
                Reprise suivante
              </Button>
            </Grid>
            <Grid item xs={12}>
              <Button fullWidth variant="contained" color="success" onClick={() => finishMatch()} sx={{ minHeight: 52, fontWeight: 700 }}>
                Terminer et voir le résultat
              </Button>
            </Grid>
          </Grid>
        </Box>
      </Paper>
    </Box>
  );
}